import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const LOGO = 'https://storage.readdy-site.link/project_files/0f27b860-7899-4d17-a451-e043696e780c/51ae8833-6174-4697-a4e4-f9113fa91181_cargonet_PNG.png?v=3ebb7523aaf49333dc87e7813a9a53fc';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { isDark } = useTheme();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      await fetch('https://readdy.ai/api/form/d76i815r5g02gnd6a190', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });
    } catch { /**/ }
    setSent(true);
    setEmail('');
  };

  return (
    <footer
      style={{
        background: 'var(--footer-bg)',
        borderTop: '1px solid var(--footer-border)',
        fontFamily: 'var(--font-b, "Plus Jakarta Sans", sans-serif)',
        transition: 'background 0.4s ease',
      }}
    >
      {/* Top CTA strip */}
      <div style={{ borderBottom: '1px solid var(--footer-border)', padding: '40px 20px' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--orange)', marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Hızlı Başlangıç
            </p>
            <h3 style={{ fontFamily: 'var(--font-h, "Bricolage Grotesque", sans-serif)', fontWeight: 800, fontSize: 'clamp(24px,3vw,36px)', color: 'var(--footer-heading)', lineHeight: 1.1 }}>
              Lojistik çözümleriniz için buradayız.
            </h3>
          </div>
          <Link to="/iletisim" className="btn-primary flex-shrink-0 cursor-pointer">
            Teklif Al <i className="ri-arrow-right-line bounce-x"></i>
          </Link>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-5 cursor-pointer">
              <img
                src={LOGO}
                alt="CargoNet"
                className={`h-8 w-auto object-contain transition-all duration-400 ${isDark ? 'brightness-0 invert' : ''}`}
              />
            </Link>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--footer-text)', maxWidth: 280 }}>
              Türkiye&apos;den dünyaya lojistik köprüsü. 15+ yıllık deneyim, 24 ülke.
            </p>
            <div className="flex gap-2 mb-6">
              {[
                { icon: 'ri-instagram-line', href: 'https://www.instagram.com/cargonet.tr' },
                { icon: 'ri-linkedin-box-line', href: 'https://www.linkedin.com/company/cargonet' },
                { icon: 'ri-youtube-line', href: 'https://www.youtube.com/@cargonet' },
                { icon: 'ri-twitter-x-line', href: 'https://twitter.com/cargonettr' },
              ].map((s, i) => (
                <a
                  key={i} href={s.href} rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-300"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--footer-text)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(232,97,26,0.2)';
                    e.currentTarget.style.color = 'var(--orange)';
                    e.currentTarget.style.borderColor = 'rgba(232,97,26,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--surface)';
                    e.currentTarget.style.color = 'var(--footer-text)';
                    e.currentTarget.style.borderColor = 'var(--border)';
                  }}
                >
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>
            <div className="space-y-2">
              {[
                { icon: 'ri-phone-line', text: '0850 242 0 822', href: 'tel:+908502420822' },
                { icon: 'ri-whatsapp-line', text: '0505 891 6 367', href: 'https://wa.me/905058916367' },
                { icon: 'ri-mail-line', text: 'cs@cargonet.com.tr', href: 'mailto:cs@cargonet.com.tr' },
              ].map((c, i) => (
                <a
                  key={i} href={c.href} rel="nofollow"
                  className="flex items-center gap-2 text-sm cursor-pointer transition-colors duration-300"
                  style={{ color: 'var(--footer-text)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--orange)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--footer-text)')}
                >
                  <i className={`${c.icon} text-[#E8611A]`}></i> {c.text}
                </a>
              ))}
            </div>
          </div>

          {/* Hizmetler */}
          <div className="lg:col-span-2">
            <h4 className="text-xs mb-5" style={{ color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              <Link to="/hizmetler" style={{ color: 'inherit', textDecoration: 'none' }}>Hizmetler</Link>
            </h4>
            <ul className="space-y-2.5">
              {[
                { l: 'Ekspres Kurye', p: '/hizmetler/ekspres-kurye-servisi' },
                { l: 'E-Ticaret Lojistiği', p: '/hizmetler/uluslararasi-e-ticaret-servisi' },
                { l: 'Kargo Taşımacılığı', p: '/hizmetler/uluslararasi-kargo-tasimaciligi' },
                { l: 'Fulfillment', p: '/hizmetler/fulfillment' },
                { l: 'Mikro İhracat', p: '/hizmetler/mikro-ihracat-etgb' },
                { l: 'A.TR Belgesi', p: '/hizmetler/atr-belgesi' },
                { l: 'Sigorta', p: '/hizmetler/sigorta' },
              ].map((item) => (
                <li key={item.l}>
                  <Link
                    to={item.p}
                    className="text-sm cursor-pointer transition-colors duration-300"
                    style={{ color: 'var(--footer-text)', textDecoration: 'none' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--orange)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--footer-text)')}
                  >
                    {item.l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Kurumsal */}
          <div className="lg:col-span-2">
            <h4 className="text-xs mb-5" style={{ color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              <Link to="/hakkimizda" style={{ color: 'inherit', textDecoration: 'none' }}>Kurumsal</Link>
            </h4>
            <ul className="space-y-2.5">
              {[
                { l: 'Hakkımızda', p: '/hakkimizda' },
                { l: 'Filo & Teknoloji', p: '/filo-ve-teknoloji' },
                { l: 'Referanslar', p: '/referanslar' },
                { l: 'İletişim', p: '/iletisim' },
                { l: 'KVKK', p: '/iletisim' },
              ].map((item) => (
                <li key={item.l}>
                  <Link
                    to={item.p}
                    className="text-sm cursor-pointer transition-colors duration-300"
                    style={{ color: 'var(--footer-text)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--orange)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--footer-text)')}
                  >
                    {item.l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="lg:col-span-4">
            <h4 className="text-xs mb-5" style={{ color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Bülten
            </h4>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--footer-text)' }}>
              Lojistik haberleri ve özel kampanyalar için abone olun.
            </p>
            {sent ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--orange)', fontWeight: 700 }}>
                <i className="ri-check-double-line"></i> Teşekkürler, abone oldunuz!
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  placeholder="E-posta adresiniz" className="inp flex-1"
                />
                <button
                  type="submit"
                  className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-300"
                  style={{ background: 'var(--orange)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--orange2)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--orange)')}
                >
                  <i className="ri-arrow-right-line text-white"></i>
                </button>
              </form>
            )}
            <div className="mt-6 flex items-start gap-2 text-sm" style={{ color: 'var(--text3)' }}>
              <i className="ri-map-pin-line text-[#E8611A] mt-0.5 flex-shrink-0"></i>
              Turgut Özal Mah. 55.Sk. No:47/3 Esenyurt / İSTANBUL
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-12 pt-6" style={{ borderTop: '1px solid var(--footer-border)' }}>
          <p className="text-xs" style={{ color: 'var(--text3)' }}>© 2025 CargoNet. Tüm Hakları Saklıdır.</p>
          <p className="text-xs" style={{ color: 'var(--text3)' }}>Gizlilik Politikası · Kullanım Koşulları</p>
        </div>
      </div>
    </footer>
  );
}
